import { createElement, useCallback, useEffect, useMemo, useState } from 'react';
import type { ComponentType, ReactElement } from 'react';
import type { Condition, LibellesDeCondition } from '@taki/data-model';
import type { ScreenAction } from '@taki/ui';
import type { TakiSdk } from '@taki/sdk';
import type { ActionDeFicheAssemblee } from './assemblage.js';
import type { FicheGenerique, ProprietesDeFenetreDeFiche } from './app-declaree.js';
import { EVENEMENT_PIECES_JOINTES } from './boutons-flux.js';
import type { ControleurDeBoutonsFlux } from './controleur-boutons-flux.js';
import { etatDuBouton } from './etat-des-boutons.js';

/**
 * LES GESTES DE NIVEAU FICHE QUE LA DECLARATION POSE (`niveau: "fiche"`, chantier
 * `actions-fiche-sans-plafond`, `docs/socle/actions-fiche-sans-plafond.md`).
 *
 * UN GESTE, UNE DECLARATION, TOUTES LES VUES DE LA FICHE : la rangee 3 de la
 * fiche ouverte les rend a gauche apres la sortie, sur la Fiche standard comme
 * sur une vue Canvas (qui pose les siens avant, `actions-de-vue-de-fiche.ts`).
 * Jamais sur un brouillon : `ficheOuverte` est alors nulle, et il n'y a pas
 * encore de fiche a qui donner le geste.
 *
 * SON EFFET est un `fn` d'app, appele avec le contexte habituel (`ficheId` = la
 * fiche ouverte), ou une FENETRE d'app (`fenetres` de `definirAppDeclaree`) :
 * un composant React ordinaire, monte ici a cote du gabarit, qui rend son propre
 * `Modal`. Un effet ne peut ni ouvrir de fenetre ni utiliser un hook ; la
 * fenetre, si.
 *
 * LA FENETRE SE DEMONTE d'elle-meme quand la fiche ou la porte change : elle est
 * l'affaire de CETTE fiche, pas de la suivante.
 *
 * CE SONT DES SMART BOUTONS (`docs/socle/smart-boutons.md`) : chacun peut porter
 *  - une CONDITION `si`, jugee sur la fiche ouverte BRUTE avec le langage commun
 *    (`evaluerCondition`, le fuseau de l'org, moi). Fausse, le bouton est GRISE
 *    avec sa raison (`raison`, ou la phrase derivee de la condition) ou RETIRE
 *    (`siFaux: "masquer"`) ;
 *  - une CONFIRMATION, que le gabarit ouvre avant le clic ;
 *  - une action FLUX : le clic lance les flux actifs branches (`useBoutonsFlux`) ;
 *    grise tant qu'aucun n'est branche.
 * La priorite des raisons vit dans `etat-des-boutons.ts`.
 */

interface FenetreOuverte {
  action: string;
  fiche: string;
  porte: string;
}

export interface OptionsDesActionsDeFiche {
  /** Les gestes de niveau fiche de la porte active. */
  actions: readonly ActionDeFicheAssemblee[];
  /** Les fenetres de l'app, par leur nom. */
  fenetres: Readonly<Record<string, ComponentType<ProprietesDeFenetreDeFiche>>>;
  /** L'id de la porte active. */
  porte: string;
  /** L'id de la fiche ouverte, `null` sur un brouillon, un modele ou sans fiche. */
  ficheOuverte: string | null;
  /** La fiche ouverte, brute, `undefined` tant qu'elle n'est pas lue. */
  fiche: FicheGenerique | undefined;
  /** La raison pour laquelle la porte ne s'ecrit pas (`useScreen().raisonAcces`) : les gestes s'eteignent avec elle (G16). */
  raisonAcces: string | null;
  /** Les smart boutons flux de l'ecran (le clic, l'etat « en cours », les fenetres). */
  flux: ControleurDeBoutonsFlux;
  /** `assemble.evaluer(porte, contexte)` : la lecture des conditions de la fiche ouverte. Absente : aucune condition ne se juge. */
  evaluer?: (condition: Condition, fiche: FicheGenerique, surcharge?: (nom: string) => unknown) => boolean;
  /** Les mots de l'ecran pour la phrase d'une condition (libelle d'un champ, d'une valeur). */
  libelles: LibellesDeCondition;
  /** La liste des pieces jointes de la fiche ouverte, quand une condition la cite (`usePiecesJointes`). */
  piecesJointes?: readonly unknown[] | 'chargement' | 'erreur' | undefined;
}

/**
 * LES PIECES JOINTES PRETES DE LA FICHE OUVERTE, lues SEULEMENT quand une
 * condition de la porte cite `socle:piecesJointes` (`actif`) : une porte sans
 * cette condition ne fait aucune lecture de plus. `undefined` tant qu'on ne les
 * demande pas, `'chargement'` pendant la lecture, `'erreur'` si elle echoue.
 * Elle se relit quand le panneau des pieces jointes ajoute ou retire un fichier.
 */
export function usePiecesJointes(options: {
  sdk: TakiSdk;
  /** La famille de la fiche, la `collection` de ses fichiers. */
  famille: string | null;
  ficheOuverte: string | null;
  actif: boolean;
}): readonly unknown[] | 'chargement' | 'erreur' | undefined {
  const { sdk, famille, ficheOuverte, actif } = options;
  const [lue, setLue] = useState<{ cle: string; liste: readonly unknown[] | 'erreur' } | null>(null);
  const [relecture, setRelecture] = useState(0);
  const cle = famille === null || ficheOuverte === null ? null : `${famille}:${ficheOuverte}`;
  useEffect(() => {
    if (!actif) return;
    const relire = (): void => setRelecture((n) => n + 1);
    window.addEventListener(EVENEMENT_PIECES_JOINTES, relire);
    return () => window.removeEventListener(EVENEMENT_PIECES_JOINTES, relire);
  }, [actif]);
  useEffect(() => {
    if (!actif || cle === null || famille === null || ficheOuverte === null) return;
    let vivant = true;
    sdk.files.list({ collection: famille, id: ficheOuverte }, { limit: 50 }).then(
      (fichiers) => { if (vivant) setLue({ cle, liste: fichiers.filter((fichier) => fichier.status === 'ready') }); },
      (cause: unknown) => {
        console.warn('[taki/app-runtime] Les pieces jointes de la fiche sont illisibles.', cause);
        if (vivant) setLue({ cle, liste: 'erreur' });
      },
    );
    return () => { vivant = false; };
  }, [sdk, actif, cle, famille, ficheOuverte, relecture]);
  if (!actif || cle === null) return undefined;
  return lue !== null && lue.cle === cle ? lue.liste : 'chargement';
}

/**
 * Les entrees de la rangee 3 de la fiche, et la fenetre ouverte (a rendre en
 * frere du gabarit), `null` quand aucune ne l'est.
 */
export function useActionsDeFicheDeclarees(options: OptionsDesActionsDeFiche): {
  rangee: ScreenAction[];
  fenetre: ReactElement | null;
} {
  const { actions, fenetres, porte, ficheOuverte, fiche, raisonAcces, flux, evaluer, libelles, piecesJointes } = options;
  const [ouverte, setOuverte] = useState<FenetreOuverte | null>(null);
  const valable = ouverte !== null && ouverte.fiche === ficheOuverte && ouverte.porte === porte;
  /* LA FENETRE DE LA FICHE PRECEDENTE NE REVIENT PAS avec elle : l'etat tombe,
     il ne se contente pas de ne plus se rendre. */
  useEffect(() => {
    if (ouverte !== null && !valable) setOuverte(null);
  }, [ouverte, valable]);
  const fermer = useCallback(() => setOuverte(null), []);

  const rangee = useMemo<ScreenAction[]>(() => {
    if (ficheOuverte === null) return [];
    return actions.flatMap((action): ScreenAction[] => {
      /* UNE FENETRE QUE L'APP NE FOURNIT PAS EST UN REFUS, comme un effet absent
         (`actionsDe`) : le montage de l'app l'a deja tenu pour sa propre
         declaration, ceci attrape celle qu'une org a ecrite. Un bouton muet
         serait un bouton qui ment. Dans une INSTANCE d'org, l'assemblage a deja
         pose `scriptAbsent` : le bouton est grise, il ne fait pas tomber l'app. */
      if (action.scriptAbsent === undefined && action.fenetre !== undefined && fenetres[action.fenetre] === undefined) {
        throw new Error(`L'action "${action.id}" nomme la fenetre "${action.fenetre}", que l'app ne fournit pas.`);
      }
      const etat = etatDuBouton({
        action,
        raisonAcces,
        scriptAbsent: action.scriptAbsent ?? null,
        branchement: action.flux === true ? flux.etat(porte, action.id) : undefined,
        fiche,
        evaluer,
        libelles,
        piecesJointes,
      });
      if (etat.masque) return [];
      const entree: ScreenAction = {
        id: action.id,
        label: action.label,
        ...(action.variant === undefined ? {} : { variant: action.variant }),
        disabled: etat.raison !== null,
        ...(etat.raison === null ? {} : { disabledReason: etat.raison }),
        /* LA CONFIRMATION D'UN BOUTON FLUX N'EST PAS CELLE DU GABARIT : le
           controleur l'ouvre, UNE fois, avec les envois hors de l'equipe. */
        ...(action.confirm === undefined || action.flux === true ? {} : { confirm: action.confirm }),
        ...(action.flux === true && flux.occupe(porte, action.id) ? { loading: true } : {}),
        onClick: () => {
          if (action.flux === true) {
            flux.lancer(
              { porteId: porte, boutonId: action.id, label: action.label, confirmation: action.confirm },
              ficheOuverte,
            );
          } else if (action.fenetre !== undefined) setOuverte({ action: action.id, fiche: ficheOuverte, porte });
          else action.lancer?.();
        },
      };
      return [entree];
    });
  }, [actions, fenetres, ficheOuverte, porte, raisonAcces, flux, fiche, evaluer, libelles, piecesJointes]);

  const courante = valable ? actions.find((action) => action.id === ouverte.action) : undefined;
  const Fenetre = courante?.fenetre === undefined ? undefined : fenetres[courante.fenetre];
  const fenetre =
    Fenetre === undefined || fiche === undefined || ouverte === null
      ? null
      : createElement(Fenetre, { key: `${ouverte.action}:${ouverte.fiche}`, fiche, porte, fermer });
  return { rangee, fenetre };
}
